import { Box, Grid } from '@mui/material';
import { FormProvider, useFormContext } from 'react-hook-form';
import { FC, Fragment, useCallback, useContext, useEffect, useState } from 'react';
// components
import { Alert } from 'components/common/Alert';
import PackageNCost from '../../Dosage/Add/components/PackageNCost';
import FactSheetModal from '../../Dosage/Add/components/FactSheetModal';
import IngredientsList from '../../Dosage/Add/components/IngredientsList';
import FormulaInfoCard from '../../Dosage/Add/components/FormulaInfoCard';
// constants, schemas, interfaces, context
import {
  ProductTypes,
  BottleCapType,
  BottleTypeEnum,
  CalcFormulaCostPayload,
  useCalcFormulaCostLazyQuery,
  CoMfgCostType,
} from 'generated/graphql';
import { HTTP_STATUS } from 'constants/index';
import { ActionType } from 'reducer/addFactBoxReducer';
import { formatAgeGroups, formatCalcFormulaIngredients } from 'lib/helper';
import { FORM_FILLED_ERROR_MESSAGE_TEXT } from 'constants/constant';
import { FormulaIngredientContext } from 'contexts/FormulaByIngredientContext';
import { BlendItemType, CalcFormulaCost, FactBoxStep3Props, FactBoxFormulationFormType } from 'interfaces';

const Step3: FC<FactBoxStep3Props> = ({ formulaMethods }) => {
  // local states
  const [open, setOpen] = useState(false);
  const [formulaCost, setFormulaCost] = useState<CalcFormulaCost>(null);

  const { state, dispatch } = useContext(FormulaIngredientContext);
  const ingredientMethods = useFormContext<FactBoxFormulationFormType>();

  const { trigger, watch } = ingredientMethods;
  const { formulaIngredients, capOverride } = watch();

  const { productType, productFormulationType, formulaInfo, subProductType, ingredientsBlend } = state;

  const { value: productTypeId, type } = productType || {};
  const { value: subProductTypeId } = subProductType || {};
  const {
    bottleCap,
    bottleType,
    coMfgCostType,

    coMfgCostPercentage,
    factBoxType,

    productSize: productSizeType,

    productSubType,

    sandCoating,
    tabletCoating,

    excipientType,

    flavorSystem,
    sweetenerSystem,

    capsuleType,
    innerCapsuleType,
    outerCapsuleType,

    servingUnit,
    desiredServingSize,
    servingPerContainer,
    miscellaneousCost,
    description,
    ageGroups,
  } = formulaInfo || {};

  const { value: productSizeId } = productSizeType || {};
  const { value: excipientTypeId } = excipientType || {};

  const { value: sweetenerSystemId } = sweetenerSystem || {};

  const { value: productFormulationTypeId } = productFormulationType || {};

  const isValid = formulaIngredients.every((item) => item?.value && item?.type);

  const [getFormulaCost, { loading }] = useCalcFormulaCostLazyQuery({
    onCompleted: (res) => {
      const { calcFormulaCost } = res || {};
      const { formulaCostSuggestion, response } = calcFormulaCost || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setFormulaCost(formulaCostSuggestion as CalcFormulaCostPayload['formulaCostSuggestion']);
      } else {
        setFormulaCost(null);
      }
    },
    onError: ({ message }) => {
      setFormulaCost(null);
      Alert.error(message);
    },
  });

  const handleFactSheet = async () => {
    const isValid = await trigger();
    if (isValid) {
      setOpen(!open);
    } else {
      Alert.error(FORM_FILLED_ERROR_MESSAGE_TEXT);
    }
  };

  const fetchFormulaCost = useCallback(async () => {
    const isValid = await trigger();
    if (isValid) {
      const ingredients = formatCalcFormulaIngredients(formulaIngredients);
      const formattedAgeGroups = formatAgeGroups(ageGroups ?? []);

      const ingredientBlends = ingredientsBlend?.map(({ ingredients, name: blendName }) => {
        return {
          name: blendName,
          formulaIngredients: formatCalcFormulaIngredients(ingredients),
        };
      });
      if (formulaIngredients?.length || ingredientBlends?.length) {
        await getFormulaCost({
          variables: {
            calcFormulaCostInput: {
              formulaIngredients: ingredients,
              formulaBlends: ingredientBlends,

              capOverride,

              ...(bottleCap && { bottleCap: bottleCap as BottleCapType }),
              ...(bottleType && { bottleType: bottleType as BottleTypeEnum }),

              ...(coMfgCostType && { coMfgCostType: coMfgCostType as CoMfgCostType }),
              ...(coMfgCostPercentage && { coMfgCostPercentage: Number(coMfgCostPercentage) }),

              ageGroups: formattedAgeGroups,

              productTypeId: productTypeId || '',
              productFormulationTypeId: productFormulationTypeId || '',
              ...(productSubType && { subProductTypeId: productSubType }),

              ...(excipientTypeId && { excipientTypeId: excipientTypeId }),

              ...(sandCoating && { sandCoatingId: sandCoating }),
              ...(tabletCoating && { tabletCoatingId: tabletCoating }),

              ...(productSizeId && { productSizeId }),

              ...(flavorSystem?.value && { flavorSystemId: flavorSystem?.value }),
              ...(sweetenerSystemId && { sweetenerSystemId: sweetenerSystemId }),

              ...(capsuleType && { capsuleTypeId: capsuleType }),
              ...(innerCapsuleType && { innerCapsuleTypeId: innerCapsuleType }),
              ...(outerCapsuleType && { outerCapsuleTypeId: outerCapsuleType }),

              ...(miscellaneousCost && { miscellaneousCost: Number(miscellaneousCost) }),
              ...(description && { description }),
            },
          },
        });
      } else {
        setFormulaCost(null);
      }
    } else {
      Alert.error(FORM_FILLED_ERROR_MESSAGE_TEXT);
    }
  }, [
    bottleCap,
    bottleType,
    capsuleType,
    excipientTypeId,
    flavorSystem?.value,
    formulaIngredients,
    getFormulaCost,
    ingredientsBlend,
    innerCapsuleType,
    outerCapsuleType,
    productFormulationTypeId,
    productSubType,
    productTypeId,
    sandCoating,
    ageGroups,
    sweetenerSystemId,
    productSizeId,
    tabletCoating,
    coMfgCostType,
    coMfgCostPercentage,
    capOverride,
    trigger,
    miscellaneousCost,
    description,
  ]);

  const onBlendAdd = (data: BlendItemType[]) => {
    dispatch({ type: ActionType.SET_INGREDIENTS_BLEND, ingredientsBlend: data });
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (isValid) {
      timeoutId = setTimeout(() => {
        fetchFormulaCost();
      }, 1000);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [fetchFormulaCost, isValid]);

  return (
    <Fragment>
      <Grid container spacing={3}>
        <Grid item xs={12} xl={8}>
          <Box mb={2}>
            <FormProvider {...formulaMethods}>
              <FormulaInfoCard />
            </FormProvider>
          </Box>

          <IngredientsList
            isShowCategory
            onBlendAdd={onBlendAdd}
            handleFactSheet={handleFactSheet}
            productType={type as ProductTypes}
            ingredientsBlend={ingredientsBlend}
            formulaIngredients={formulaIngredients}
            productFormulationType={productFormulationType}
          />
        </Grid>
        <Grid item xs={12} xl={4}>
          <PackageNCost
            calculateCost={fetchFormulaCost}
            formulaCost={formulaCost}
            loading={loading}
            bottleCap={bottleCap}
            productType={productType}
            subProductType={subProductType}
            productFormulationType={productFormulationType}
          />
        </Grid>
      </Grid>

      <FactSheetModal
        open={open}
        ageGroups={ageGroups}
        factBoxType={factBoxType}
        servingUnit={servingUnit}
        capsuleTypeId={capsuleType}
        productTypeId={productTypeId}
        onClose={() => setOpen(!open)}
        ingredientsBlend={ingredientsBlend}
        innerCapsuleTypeId={innerCapsuleType}
        outerCapsuleTypeId={outerCapsuleType}
        excipientTypeId={excipientTypeId}
        formulaIngredients={formulaIngredients}
        desiredServingSize={desiredServingSize}
        tabletCoating={tabletCoating}
        servingPerContainer={servingPerContainer}
        sweetenerSystemId={sweetenerSystemId}
        productFormulationTypeId={productFormulationTypeId}
        productSizeId={productSizeId}
        sandCoatingId={sandCoating}
        subProductTypeId={subProductTypeId}
      />
    </Fragment>
  );
};

export default Step3;
